// =============================================================================
// app/create-post.tsx — CRYB rich post composer, Apple-aligned (P2-COMPOSER).
//
// This is the navigator entry point for the "CreatePost" modal route (App.tsx
// imports `./app/create-post`). It was previously a thin re-export of
// screens/CreatePostScreen → components/create/CreatePost, whose shell used a
// flat colors.bg background behind an opaque native header.
//
// This screen brings the composer up to the shipped CRYB Apple standard
// (matches CreateCommunityScreen / Home / Search / Notifications / DM):
//   • Full-bleed <ParticleBackground/> mounted absoluteFill as the FIRST child;
//     the container is transparent so the aurora shows through (skill §12).
//   • The App.tsx native header is made translucent locally (headerTransparent),
//     so content scrolls UNDER it — no opaque bar, no gap (skill §12).
//   • Header "Post" action preserved (headerRight) AND a pinned frosted commit
//     footer so the primary action is always reachable in the modal (§16).
//   • Every surface is a Glass* primitive; spacing/radius/typography/shadow come
//     from constants/theme + glass tokens. Large title tightened (§15).
//
// BACKEND CONTRACT IS UNCHANGED — same POST /posts + uploadMedia flow, same
// community routing rules, same media picker, same accessibility. This is a
// visual/UX upgrade only; the create/upload path keeps working exactly as
// before. (Logic ported verbatim from components/create/CreatePost.tsx so the
// only edited file is this route module.)
// =============================================================================
import React, { useState, useLayoutEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TextInput,
  TouchableOpacity,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Image,
  Modal,
  Pressable,
  useWindowDimensions,
} from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { launchImageLibrary } from 'react-native-image-picker';
import { Ionicons, IoniconName } from '../components/ui/Icon';
import { GlassSurface, GlassSheet, GlassButton, glassTokens } from '../components/glass';
import ParticleBackground from '../components/ui/ParticleBackground';
import ScreenHeading from '../components/ui/ScreenHeading';
import {
  colors,
  spacing,
  radius,
  fonts,
  typographyScale,
  getLayout,
  layout as layoutTokens,
} from '../constants/theme';
import { api, uploadMedia, getLastUploadError } from '../services/api';
import { tabularNums } from '../lib/format';
import { pushLocalPost } from '../services/feedEvents';
import type { RootStackParamList } from '../navigation/types';

type Nav = NativeStackNavigationProp<RootStackParamList>;

type PostType = 'post' | 'article';
type MediaKind = 'image' | 'video';
type Attachment = { uri: string; kind: MediaKind };
type Community = { id: string; name: string; displayName: string };

const POST_TYPES: { key: PostType; label: string; icon: IoniconName }[] = [
  { key: 'post', label: 'Post', icon: 'chatbubble-outline' },
  { key: 'article', label: 'Article', icon: 'document-text-outline' },
];

const CHAR_LIMIT: Record<PostType, number> = { post: 280, article: 10000 };

export default function CreatePost() {
  const navigation = useNavigation<Nav>();
  const insets = useSafeAreaInsets();
  // Optional entry flag (CreateChooser's "Article" row passes { type:'article' })
  // seeds the toggle; plain navigate('CreatePost') → 'post'.
  const route = useRoute<RouteProp<RootStackParamList, 'CreatePost'>>();
  const { width } = useWindowDimensions();
  const responsiveLayout = getLayout(width);
  const containerStyle =
    responsiveLayout !== 'phone'
      ? {
          maxWidth: responsiveLayout === 'desktop' ? 720 : 620,
          alignSelf: 'center' as const,
          width: '100%' as const,
        }
      : undefined;

  const [postType, setPostType] = useState<PostType>(
    route.params?.type === 'article' ? 'article' : 'post',
  );
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  // Track BOTH the community id (cuid the backend honors as `communityId`) and
  // its name (for the c/<name> label). The backend ignores a `community` name
  // field and posts to the general feed; it only routes by `communityId`.
  const [community, setCommunity] = useState<{ id: string; name: string } | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [communities, setCommunities] = useState<Community[]>([]);
  const [loadingCommunities, setLoadingCommunities] = useState(false);
  // Distinguish a load FAILURE from a genuinely empty list (was conflated as
  // "you haven't joined any communities yet").
  const [communitiesError, setCommunitiesError] = useState(false);
  const [attachment, setAttachment] = useState<Attachment | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const maxChars = CHAR_LIMIT[postType];
  const used = content.length;
  const charsLeft = maxChars - used;
  const progress = Math.min(1, used / maxChars);
  const canSubmit =
    content.trim().length > 0 &&
    charsLeft >= 0 &&
    (postType === 'post' || title.trim().length > 0);

  const counterColor =
    charsLeft < 0 ? colors.error : charsLeft < 20 ? colors.warning : colors.muted;

  // ── Community sheet ─────────────────────────────────────────────────────────
  const openCommunitySheet = useCallback(async () => {
    setSheetOpen(true);
    if (communities.length > 0) return;
    setLoadingCommunities(true);
    setCommunitiesError(false);
    try {
      // Backend wraps the list as { data: { items: [...] } }; api() unwraps one
      // level, so the array lives at res.data.items (NOT res.data directly).
      const res = await api<{ items: Community[] }>('/communities');
      if (res.success && res.data) {
        setCommunities(Array.isArray(res.data.items) ? res.data.items : []);
      } else {
        setCommunitiesError(true);
      }
    } catch {
      setCommunitiesError(true);
    } finally {
      setLoadingCommunities(false);
    }
  }, [communities.length]);

  // ── Media ───────────────────────────────────────────────────────────────────
  const pickMedia = useCallback(async (kind: MediaKind) => {
    try {
      // react-native-image-picker: launchImageLibrary returns
      // { didCancel, errorCode, assets:[{uri,type,...}] }.
      const result = await launchImageLibrary({
        mediaType: kind === 'video' ? 'video' : 'photo',
        quality: 0.8,
        selectionLimit: 1,
        videoQuality: 'high',
      });
      if (result.didCancel) return;
      if (result.errorCode) {
        Alert.alert('Error', result.errorMessage || 'Could not open the picker');
        return;
      }
      const uri = result.assets?.[0]?.uri;
      if (uri) setAttachment({ uri, kind });
    } catch {
      Alert.alert('Error', 'Could not open the media picker');
    }
  }, []);

  // ── Submit (unchanged backend contract) ──────────────────────────────────────
  const handleSubmit = useCallback(async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);

    try {
      const body: Record<string, any> = { content: content.trim(), type: postType };
      if (postType === 'article' && title.trim()) body.title = title.trim();

      let uploadedUrl: string | null = null;
      if (attachment) {
        // The existing uploadMedia route accepts image/video/audio (100MB bucket).
        uploadedUrl = await uploadMedia(attachment.uri);
        if (!uploadedUrl) {
          setSubmitting(false);
          Alert.alert(
            'Upload Failed',
            getLastUploadError() || 'Could not upload the media. Please try again.',
          );
          return;
        }
        body.mediaUrls = [uploadedUrl];
      }

      // Community posts route by `communityId` (the cuid) — a `community` name is
      // ignored by the backend. When targeting a community the backend requires
      // `type` to be one of text|link|image|video|poll (it rejects post/article),
      // and the user must be a member (else a 403).
      if (community) {
        body.communityId = community.id;
        body.type = uploadedUrl
          ? attachment?.kind === 'video'
            ? 'video'
            : 'image'
          : 'text';
      }

      const res = await api('/posts', { method: 'POST', body: JSON.stringify(body) });
      setSubmitting(false);

      if (res.success) {
        // Prepend the just-created post into the home feed live (no wait for the
        // focus-refetch). Deduped in feedEvents against the server echo.
        try {
          const created: any = (res as any).data ?? {};
          const author = created.author ?? created.User ?? {};
          pushLocalPost({
            id: created.id,
            content: created.content ?? body.content,
            createdAt: created.createdAt ?? new Date().toISOString(),
            repostCount: 0,
            _count: { Vote: 0, Comment: 0 },
            User: {
              id: author.id,
              username: author.username,
              displayName: author.displayName ?? author.display_name,
              avatar: author.avatar ?? author.avatar_url,
            },
          });
        } catch {
          /* non-fatal: focus-refetch still shows the post */
        }
        // No blocking "Posted" alert — the post is already dispatched to the feed
        // and we return straight to it (premium apps don't interrupt on success).
        navigation.goBack();
      } else {
        Alert.alert('Error', res.error || 'Failed to create post');
      }
    } catch (err: any) {
      setSubmitting(false);
      Alert.alert('Error', err?.message || 'Something went wrong. Please try again.');
    }
  }, [canSubmit, submitting, content, postType, title, attachment, community, navigation]);

  // §12 Translucent chrome: make THIS modal's header transparent so the
  // full-bleed ParticleBackground shows through and content scrolls under it
  // (the shared App.tsx headerStyle is opaque colors.bg — override locally).
  // The header "Post" action is preserved exactly (same handler/disabled/a11y).
  useLayoutEffect(() => {
    navigation.setOptions({
      headerTransparent: true,
      headerStyle: { backgroundColor: 'transparent' },
      headerShadowVisible: false,
      headerTintColor: colors.primary,
      // Clear the nav-bar title — the in-screen large "New post/article" title is
      // the single source of hierarchy (§15). Keeping both was a duplicate header.
      headerTitle: '',
      headerTitleStyle: { ...fonts.semibold, fontSize: 17, color: colors.ink },
      headerRight: () => (
        <TouchableOpacity
          onPress={handleSubmit}
          disabled={!canSubmit || submitting}
          style={[styles.postBtn, (!canSubmit || submitting) && styles.postBtnDisabled]}
          accessibilityRole="button"
          accessibilityLabel="Publish post"
        >
          {submitting ? (
            <ActivityIndicator size="small" color={colors.white} />
          ) : (
            <Text style={styles.postBtnText}>Post</Text>
          )}
        </TouchableOpacity>
      ),
    });
  }, [navigation, handleSubmit, canSubmit, submitting]);

  const communityLabel = community ? `c/${community.name}` : 'General feed';

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      {/* §12 Full-bleed aurora — mounted first, under the status bar + header. */}
      <ParticleBackground />

      <ScrollView
        contentContainerStyle={[
          styles.scrollContent,
          containerStyle,
          {
            // Clear the translucent nav bar (content scrolls under it)…
            paddingTop: insets.top + layoutTokens.headerHeight + spacing.md,
            // …and clear the pinned frosted commit footer at the bottom.
            paddingBottom: 54 + insets.bottom + spacing.xxl,
          },
        ]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {/* Composer identity — a tightened large title builds hierarchy from
            weight+size (§15), replacing the bare route-name header alone. */}
        <ScreenHeading
          style={styles.intro}
          title={`New ${postType === 'article' ? 'article' : 'post'}`}
          subtitle="Share it with the CRYB community."
        />
        {/* (intro title/subtitle now render via the shared ScreenHeading — the
            single on-brand screen-title treatment, replacing the raw flat-ink
            <Text> pair per the CRYB design system.) */}

        {/* Post / Article toggle */}
        <GlassSurface variant="pill" radius={glassTokens.radius.pill} contentStyle={styles.segment}>
          {POST_TYPES.map((t) => {
            const active = postType === t.key;
            return (
              <Pressable
                key={t.key}
                onPress={() => setPostType(t.key)}
                style={({ pressed }) => [
                  styles.segmentItem,
                  active && styles.segmentItemActive,
                  pressed && !active && { opacity: 0.7 },
                ]}
                accessibilityRole="button"
                accessibilityState={{ selected: active }}
                accessibilityLabel={`${t.label} mode`}
              >
                <Ionicons
                  name={t.icon}
                  size={16}
                  color={active ? colors.white : colors.ink2}
                />
                <Text style={[styles.segmentText, active && styles.segmentTextActive]}>
                  {t.label}
                </Text>
              </Pressable>
            );
          })}
        </GlassSurface>

        {/* Target chip */}
        <TouchableOpacity
          activeOpacity={0.85}
          onPress={openCommunitySheet}
          accessibilityRole="button"
          accessibilityLabel="Choose where to post"
        >
          <GlassSurface variant="card" radius={radius.lg} contentStyle={styles.targetRow}>
            <View style={styles.targetIcon}>
              <Ionicons
                name={community ? 'people' : 'globe-outline'}
                size={18}
                color={colors.primary}
              />
            </View>
            <View style={styles.targetTextWrap}>
              <Text style={styles.targetLabel}>Posting to</Text>
              <Text style={styles.targetValue} numberOfLines={1}>
                {communityLabel}
              </Text>
            </View>
            <Ionicons name="chevron-down" size={18} color={colors.muted} />
          </GlassSurface>
        </TouchableOpacity>

        {/* Article title */}
        {postType === 'article' && (
          <GlassSurface variant="card" radius={radius.lg} contentStyle={styles.titleCard}>
            <TextInput
              style={styles.titleInput}
              placeholder="Article title"
              placeholderTextColor={colors.muted}
              value={title}
              onChangeText={setTitle}
              maxLength={200}
            />
          </GlassSurface>
        )}

        {/* Body */}
        <GlassSurface variant="card" radius={radius.lg} contentStyle={styles.bodyCard}>
          <TextInput
            style={styles.bodyInput}
            placeholder={
              postType === 'article'
                ? 'Write your article…'
                : "What's happening in crypto?"
            }
            placeholderTextColor={colors.muted}
            multiline
            maxLength={maxChars}
            value={content}
            onChangeText={setContent}
            textAlignVertical="top"
            autoFocus
          />

          {/* Attachment preview */}
          {attachment && (
            <View style={styles.previewWrap}>
              {attachment.kind === 'image' ? (
                <Image source={{ uri: attachment.uri }} style={styles.previewImage} />
              ) : (
                <View style={[styles.previewImage, styles.videoTile]}>
                  <Ionicons name="film-outline" size={30} color={colors.white} />
                  <Text style={styles.videoTileText}>Video attached</Text>
                </View>
              )}
              <TouchableOpacity
                style={styles.removeMedia}
                onPress={() => setAttachment(null)}
                accessibilityRole="button"
                accessibilityLabel="Remove attachment"
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Ionicons name="close-circle" size={26} color={colors.white} />
              </TouchableOpacity>
            </View>
          )}

          {/* Counter */}
          <View style={styles.counterRow}>
            <View style={styles.counterTrack}>
              <View
                style={[
                  styles.counterFill,
                  { width: `${progress * 100}%`, backgroundColor: counterColor },
                ]}
              />
            </View>
            <Text style={[styles.counterText, tabularNums, { color: counterColor }]}>{charsLeft}</Text>
          </View>
        </GlassSurface>

        {/* Media toolbar */}
        <View style={styles.toolbar}>
          <ToolbarButton
            icon="image-outline"
            label="Photo"
            onPress={() => pickMedia('image')}
          />
          <ToolbarButton
            icon="videocam-outline"
            label="Video"
            onPress={() => pickMedia('video')}
          />
        </View>
      </ScrollView>

      {/* §12/§16 Persistent frosted commit bar — content scrolls under it, and
          the primary action is always reachable in the modal (parity with the
          shipped CreateCommunity / create surfaces). */}
      <GlassSurface
        variant="bar"
        radius={0}
        style={styles.footer}
        contentStyle={[
          styles.footerContent,
          containerStyle,
          { paddingBottom: insets.bottom + spacing.md },
        ]}
      >
        <GlassButton
          title={postType === 'article' ? 'Publish article' : 'Post'}
          icon="paper-plane-outline"
          onPress={handleSubmit}
          loading={submitting}
          disabled={!canSubmit || submitting}
          fullWidth
          size="lg"
        />
      </GlassSurface>

      {/* Community picker glass sheet */}
      <Modal
        visible={sheetOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setSheetOpen(false)}
      >
        <Pressable style={styles.backdrop} onPress={() => setSheetOpen(false)}>
          <Pressable style={styles.sheetWrap} onPress={(e) => e.stopPropagation()}>
            <GlassSheet radius={glassTokens.radius.xl}>
              <Text style={styles.sheetTitle}>Post to</Text>
              <ScrollView
                style={styles.sheetScroll}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
              >
                <CommunityRow
                  icon="globe-outline"
                  label="General feed"
                  sublabel="Everyone on CRYB"
                  active={!community}
                  onPress={() => {
                    setCommunity(null);
                    setSheetOpen(false);
                  }}
                />
                {loadingCommunities && (
                  <View style={styles.sheetLoading}>
                    <ActivityIndicator color={colors.primary} />
                  </View>
                )}
                {communities.map((c) => (
                  <CommunityRow
                    key={c.id}
                    icon="people"
                    label={`c/${c.name}`}
                    sublabel={c.displayName}
                    active={community?.id === c.id}
                    onPress={() => {
                      setCommunity({ id: c.id, name: c.name });
                      setSheetOpen(false);
                    }}
                  />
                ))}
                {!loadingCommunities && communities.length === 0 && (
                  communitiesError ? (
                    <TouchableOpacity onPress={openCommunitySheet} accessibilityRole="button" accessibilityLabel="Retry loading communities">
                      <Text style={styles.sheetEmpty}>Couldn't load communities. Tap to retry.</Text>
                    </TouchableOpacity>
                  ) : (
                    <Text style={styles.sheetEmpty}>
                      You haven't joined any communities yet.
                    </Text>
                  )
                )}
              </ScrollView>
            </GlassSheet>
          </Pressable>
        </Pressable>
      </Modal>
    </KeyboardAvoidingView>
  );
}

// ── Small presentational pieces ────────────────────────────────────────────────
function ToolbarButton({
  icon,
  label,
  onPress,
}: {
  icon: IoniconName;
  label: string;
  onPress: () => void;
}) {
  return (
    <GlassSurface
      variant="pill"
      radius={glassTokens.radius.pill}
      onPress={onPress}
      contentStyle={styles.toolbarBtn}
      accessibilityLabel={`Attach ${label}`}
    >
      <Ionicons name={icon} size={20} color={colors.primary} />
      <Text style={styles.toolbarLabel}>{label}</Text>
    </GlassSurface>
  );
}

function CommunityRow({
  icon,
  label,
  sublabel,
  active,
  onPress,
}: {
  icon: IoniconName;
  label: string;
  sublabel: string;
  active: boolean;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      activeOpacity={0.8}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityState={{ selected: active }}
      accessibilityLabel={label}
    >
      <GlassSurface
        variant="card"
        radius={radius.md}
        surfaceColor={active ? colors.primary + '18' : undefined}
        contentStyle={styles.communityRow}
      >
        <View style={styles.communityIcon}>
          <Ionicons name={icon} size={18} color={colors.primary} />
        </View>
        <View style={styles.communityTextWrap}>
          <Text style={[styles.communityLabel, active && styles.communityLabelActive]}>
            {label}
          </Text>
          <Text style={styles.communitySub} numberOfLines={1}>
            {sublabel}
          </Text>
        </View>
        {active && <Ionicons name="checkmark-circle" size={20} color={colors.primary} />}
      </GlassSurface>
    </TouchableOpacity>
  );
}

const styles = StyleSheet.create({
  // §12: transparent so the full-bleed ParticleBackground (behind) shows and
  // content scrolls under the translucent header. Vertical padding is applied
  // dynamically on scrollContent (header + footer clearance).
  container: { flex: 1, backgroundColor: 'transparent' },
  scrollContent: { paddingHorizontal: layoutTokens.gutter, gap: spacing.md },

  // Intro (large title — tightened tracking per §15)
  intro: { marginBottom: spacing.xs },
  introTitle: {
    ...typographyScale.h1,
    ...fonts.bold,
    color: colors.ink,
    letterSpacing: -0.6,
  },
  introSub: {
    ...typographyScale.body,
    color: colors.muted,
    marginTop: spacing.xs,
  },

  // Segmented toggle
  segment: { flexDirection: 'row', padding: spacing.xs, gap: spacing.xs },
  segmentItem: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
  },
  segmentItemActive: { backgroundColor: colors.primary },
  segmentText: { ...typographyScale.caption, ...fonts.semibold, color: colors.ink2 },
  segmentTextActive: { color: colors.white },

  // Target chip
  targetRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
  },
  targetIcon: {
    width: 34,
    height: 34,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '14',
  },
  targetTextWrap: { flex: 1 },
  targetLabel: { ...typographyScale.caption, color: colors.muted },
  targetValue: { ...typographyScale.body, ...fonts.semibold, color: colors.ink },

  // Title
  titleCard: { paddingHorizontal: spacing.lg, paddingVertical: spacing.md },
  titleInput: {
    ...typographyScale.h3,
    ...fonts.bold,
    color: colors.ink,
    minHeight: 40,
  },

  // Body
  bodyCard: { padding: spacing.lg, minHeight: 180 },
  bodyInput: {
    ...typographyScale.body,
    fontSize: 16,
    lineHeight: 24,
    color: colors.ink,
    minHeight: 120,
    ...fonts.regular,
  },

  // Attachment preview
  previewWrap: {
    marginTop: spacing.md,
    borderRadius: radius.md,
    overflow: 'hidden',
    position: 'relative',
  },
  previewImage: {
    width: '100%',
    height: 200,
    borderRadius: radius.md,
    backgroundColor: colors.primary + '22',
  },
  videoTile: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    backgroundColor: colors.ink,
  },
  videoTileText: { ...typographyScale.caption, ...fonts.medium, color: colors.white },
  removeMedia: {
    position: 'absolute',
    top: spacing.sm,
    right: spacing.sm,
    borderRadius: radius.full,
    backgroundColor: 'rgba(26,26,46,0.5)',
  },

  // Counter
  counterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    marginTop: spacing.md,
  },
  counterTrack: {
    flex: 1,
    height: 4,
    borderRadius: radius.full,
    backgroundColor: colors.cardBorder,
    overflow: 'hidden',
  },
  counterFill: { height: '100%', borderRadius: radius.full },
  counterText: { ...typographyScale.caption, ...fonts.semibold, minWidth: 40, textAlign: 'right' },

  // Toolbar
  toolbar: { flexDirection: 'row', gap: spacing.md },
  toolbarBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
  },
  toolbarLabel: { ...typographyScale.caption, ...fonts.semibold, color: colors.primary },

  // Header action (preserved from the original composer)
  postBtn: {
    backgroundColor: colors.primary,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    borderRadius: radius.full,
    minWidth: 60,
    alignItems: 'center',
  },
  postBtnDisabled: { opacity: 0.5 },
  postBtnText: { ...typographyScale.caption, ...fonts.bold, fontSize: 14, color: colors.white },

  // Pinned frosted commit bar (§12 scroll-edge / floating chrome).
  footer: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
  },
  footerContent: {
    paddingHorizontal: layoutTokens.gutter,
    paddingTop: spacing.md,
  },

  // Sheet
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(26,26,46,0.35)',
    justifyContent: 'flex-end',
  },
  sheetWrap: { paddingHorizontal: spacing.sm, paddingBottom: spacing.xxl },
  sheetTitle: {
    ...typographyScale.h3,
    ...fonts.bold,
    color: colors.ink,
    marginBottom: spacing.md,
  },
  sheetScroll: { maxHeight: 380 },
  sheetLoading: { paddingVertical: spacing.lg, alignItems: 'center' },
  sheetEmpty: {
    ...typographyScale.body,
    color: colors.muted,
    textAlign: 'center',
    paddingVertical: spacing.lg,
  },

  // Community row
  communityRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.md,
    paddingVertical: spacing.md,
    paddingHorizontal: spacing.md,
    marginBottom: spacing.sm,
  },
  communityIcon: {
    width: 34,
    height: 34,
    borderRadius: radius.full,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary + '14',
  },
  communityTextWrap: { flex: 1 },
  communityLabel: { ...typographyScale.body, ...fonts.semibold, color: colors.ink },
  communityLabelActive: { color: colors.primary },
  communitySub: { ...typographyScale.caption, color: colors.muted },
});
